import clsx from 'clsx';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { InvalidSvgError, sanitizeSvg } from './sanitizeSvg.ts';
import styles from './VenueMap.module.css';

interface VenueMapProps<T> {
  // Image URL. SVG with classed shape elements (rect/path/etc) → interactive
  // mode. Anything else (PNG, JPG, plain SVG, etc.) → static image mode (no
  // click handling; the access-type cards below remain interactive).
  imageUrl: string;
  imageAlt?: string;
  // EVERY section, bookable or not. A shape that matches an item is a real
  // section (tagged, dimmed when unavailable); a shape that matches none is
  // decoration and is left untouched.
  items: T[];
  // Whether a section can be selected right now. Unavailable sections render
  // dimmed and disabled; only available ones are clickable/focusable.
  isItemAvailable: (item: T) => boolean;
  // Items to highlight; a shape lights up when ANY of them maps to its section.
  selectedItems?: T[];
  // Extract the section identifier from an item. Compared (case-insensitive,
  // with spaces/underscores/dashes normalized) against the `class` attribute
  // of shape elements inside the SVG. Ignored in static-image mode.
  getItemSectionName: (item: T) => string | null | undefined;
  onSelectItem: (item: T) => void;
  // Fired when the SVG can't be loaded or parsed. Lets the parent fall back
  // to the hero image instead of showing an empty placeholder.
  onLoadError?: () => void;
  // Fired when interactive (SVG) mode is entered or left, so the parent can
  // gate copy that only applies while the clickable map is shown.
  onInteractiveChange?: (interactive: boolean) => void;
}

// Convention: class values on shape elements that mean "catch-all" — they
// match any item whose name isn't handled by any other section in the SVG.
const CATCH_ALL_SECTIONS = new Set(['outros', 'other', 'others', 'catchall']);

// SVG element types that opt into venue-map interactivity. The `[class]`
// attribute filter ensures we ignore decorative shapes (e.g. the background
// <image>) and structural wrappers like <g class="layer">.
const INTERACTIVE_SHAPE_SELECTOR =
  'svg rect[class], svg path[class], svg polygon[class], svg circle[class], svg ellipse[class]';

// Normalize section/item names to a canonical form so spaces, underscores and
// dashes don't matter: "Yellow Zone" ≡ "yellow_zone" ≡ "yellow-zone".
const slug = (value: string): string =>
  value
    .toLowerCase()
    .trim()
    .replace(/[\s_-]+/g, '_');

const isCatchAllSection = (name: string): boolean => CATCH_ALL_SECTIONS.has(slug(name));

// Exact slug equality only. Substring matching (e.g. `super_vip`.includes(`vip`))
// produces false positives when names overlap; partners that need partial
// matching must supply exact class names matching their AccessType names.
const nameMatchesSection = (
  sectionName: string,
  itemName: string | null | undefined,
  allSections: string[]
): boolean => {
  const section = slug(sectionName);

  if (isCatchAllSection(section)) {
    if (!itemName) {
      return true;
    }
    const name = slug(itemName);
    const explicit = allSections.filter((s) => !isCatchAllSection(s));
    return !explicit.some((s) => slug(s) === name);
  }

  if (!itemName) {
    return false;
  }
  return slug(itemName) === section;
};

// Inject viewBox into a width/height-only <svg> root so the SVG scales inside
// our responsive wrapper. Runtime-only — the file on disk stays untouched.
const ensureViewBox = (svgMarkup: string): string => {
  if (/<svg\b[^>]*\bviewBox/i.test(svgMarkup)) {
    return svgMarkup;
  }
  const widthMatch = svgMarkup.match(/<svg\b[^>]*\bwidth="([0-9.]+)"/i);
  const heightMatch = svgMarkup.match(/<svg\b[^>]*\bheight="([0-9.]+)"/i);
  if (!widthMatch || !heightMatch) {
    return svgMarkup;
  }
  const width = Math.round(parseFloat(widthMatch[1]));
  const height = Math.round(parseFloat(heightMatch[1]));
  return svgMarkup.replace(
    /<svg\b([^>]*)>/i,
    `<svg$1 viewBox="0 0 ${width} ${height}" preserveAspectRatio="xMidYMid meet">`
  );
};

// Authoring convention: a map author marks a shape `data-decorative="true"`
// when it accompanies a section (e.g. table-number glyphs paired with a
// selectable table marker) but is not a focus target. It is author metadata,
// read here and never stripped.
const DECORATIVE_ATTRIBUTE = 'data-decorative';

// Every attribute the sync effect owns on a shape. Decoration gets all of them
// removed so it is indistinguishable from an untouched SVG shape — including
// `aria-hidden`, which the component derives from `data-decorative` on matched
// shapes (see applyShapeState). Authors express intent with `data-decorative`,
// never `aria-hidden`, so stripping it from an unmatched shape loses nothing
// and keeps a shape that was decorative under a previous `items` from staying
// hidden once it matches no section.
const OWNED_SHAPE_ATTRIBUTES = [
  'data-selected',
  'data-available',
  'data-hover',
  'tabindex',
  'role',
  'aria-label',
  'aria-pressed',
  'aria-disabled',
  'aria-hidden',
] as const;

type ShapeState =
  | { matched: false }
  | {
      matched: true;
      available: boolean;
      selected: boolean;
      label: string;
      // `data-decorative` shapes share the section's class so CSS dims and
      // highlights them with their table, but they are not focus targets.
      decorative: boolean;
    };

// Apply one shape's section state onto the SVG element.
const applyShapeState = (element: Element, state: ShapeState): void => {
  if (!state.matched) {
    for (const attribute of OWNED_SHAPE_ATTRIBUTES) {
      element.removeAttribute(attribute);
    }
    return;
  }
  const { available, selected, label, decorative } = state;
  element.setAttribute('data-selected', available && selected ? 'true' : 'false');
  element.setAttribute('data-available', available ? 'true' : 'false');
  if (decorative) {
    // Hidden from assistive technology as a consequence of the declared
    // intent; a hand-written aria-hidden never decides interactivity.
    element.setAttribute('aria-hidden', 'true');
    for (const attribute of ['tabindex', 'role', 'aria-label', 'aria-pressed', 'aria-disabled']) {
      element.removeAttribute(attribute);
    }
    return;
  }
  element.removeAttribute('aria-hidden');
  element.setAttribute('role', 'button');
  if (available) {
    element.setAttribute('tabindex', '0');
    element.setAttribute('aria-label', `Select ${label}`);
    element.setAttribute('aria-pressed', selected ? 'true' : 'false');
    element.removeAttribute('aria-disabled');
  } else {
    // Sold-out section: keeps an accessible name and is announced as
    // disabled, but has no tabindex so it is not a keyboard stop.
    element.removeAttribute('tabindex');
    element.setAttribute('aria-label', label);
    element.setAttribute('aria-disabled', 'true');
    element.removeAttribute('aria-pressed');
  }
};

type LoadState =
  | { kind: 'loading' }
  | { kind: 'interactive'; markup: string }
  | { kind: 'static' }
  | { kind: 'error' };

function VenueMap<T>({
  imageUrl,
  imageAlt = 'Venue layout',
  items,
  isItemAvailable,
  selectedItems = [],
  getItemSectionName,
  onSelectItem,
  onLoadError,
  onInteractiveChange,
}: VenueMapProps<T>) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [load, setLoad] = useState<LoadState>({ kind: 'loading' });
  const [sections, setSections] = useState<string[]>([]);
  // Latest onLoadError captured in a ref so effects don't re-run when the
  // parent re-renders (which recreates the inline callback).
  const onLoadErrorRef = useRef(onLoadError);
  onLoadErrorRef.current = onLoadError;
  const onInteractiveChangeRef = useRef(onInteractiveChange);
  onInteractiveChangeRef.current = onInteractiveChange;

  // Report interactive vs static to the parent so it can gate copy on the
  // clickable map being shown.
  useEffect(() => {
    onInteractiveChangeRef.current?.(load.kind === 'interactive');
  }, [load.kind]);

  // Fetch the image URL once and classify it.
  useEffect(() => {
    let cancelled = false;
    setLoad({ kind: 'loading' });
    setSections([]);

    fetch(imageUrl)
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`Failed to fetch image: ${response.status}`);
        }
        const contentType = response.headers.get('content-type');
        if (
          contentType &&
          !contentType.toLowerCase().includes('svg') &&
          !contentType.toLowerCase().includes('xml')
        ) {
          return { kind: 'static' as const };
        }
        const body = await response.text();
        const looksLikeSvg =
          (contentType && contentType.toLowerCase().includes('svg')) ||
          /^\s*(<\?xml[^>]*\?>\s*)?<svg\b/i.test(body);
        if (!looksLikeSvg) {
          return { kind: 'static' as const };
        }
        return { kind: 'interactive' as const, markup: ensureViewBox(body) };
      })
      .then((next) => {
        if (!cancelled) {
          setLoad(next);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setLoad({ kind: 'error' });
          onLoadErrorRef.current?.();
        }
      });

    return () => {
      cancelled = true;
    };
  }, [imageUrl]);

  // Imperative SVG injection. We DON'T use dangerouslySetInnerHTML because
  // React re-runs that on every parent re-render (e.g. when selectedItems
  // changes), which wipes the SVG's data-selected/data-available attributes
  // and detaches the shape DOM nodes — breaking click/hover. Setting
  // innerHTML directly in a layout effect means React only manages the
  // wrapper <div>; the SVG inside is ours to keep.
  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container || load.kind !== 'interactive') {
      return;
    }
    let safeMarkup: string;
    try {
      safeMarkup = sanitizeSvg(load.markup);
    } catch (error) {
      if (error instanceof InvalidSvgError) {
        setLoad({ kind: 'error' });
        onLoadErrorRef.current?.();
        return;
      }
      throw error;
    }
    container.innerHTML = safeMarkup;
    // Tokenize the class attribute so shapes with helper classes like
    // `class="yellow_zone st0"` still register `yellow_zone` as a section.
    const seen = new Set<string>();
    container.querySelectorAll(INTERACTIVE_SHAPE_SELECTOR).forEach((element) => {
      const classes = (element.getAttribute('class') ?? '').trim().split(/\s+/).filter(Boolean);
      classes.forEach((token) => seen.add(token));
    });
    setSections(Array.from(seen));
  }, [load]);

  // Resolution rule: a section resolves to the first item, in the caller's
  // order (access targets before ticket targets), whose name matches the
  // section token AND which is available. If no matching item is available it
  // falls back to the first matching item, which then renders as the
  // unavailable (dimmed) section. So a sold-out item never shadows a bookable
  // one that shares its name, and among available matches `items` order wins.
  // This restores the pre-`items`-carries-everything behaviour, when callers
  // passed only available sections and the first match was always available.
  const findItemForSection = (sectionName: string): T | undefined => {
    const matches = items.filter((item) =>
      nameMatchesSection(sectionName, getItemSectionName(item), sections)
    );
    return matches.find((item) => isItemAvailable(item)) ?? matches[0];
  };

  // Sync selection + availability state onto every shape. Iterates shapes
  // (not sections) so multi-class shapes like `class="yellow_zone st0"`
  // resolve to the FIRST class with a matching AccessType rather than racing
  // available/unavailable updates from independent section iterations.
  useEffect(() => {
    const container = containerRef.current;
    if (!container || load.kind !== 'interactive' || sections.length === 0) {
      return;
    }

    const selectedNames = selectedItems
      .map((item) => getItemSectionName(item))
      .filter((name): name is string => Boolean(name));

    container.querySelectorAll(INTERACTIVE_SHAPE_SELECTOR).forEach((element) => {
      const classes = (element.getAttribute('class') ?? '').trim().split(/\s+/).filter(Boolean);
      let matchedSection: string | undefined;
      let matchedItem: T | undefined;
      for (const cls of classes) {
        const item = findItemForSection(cls);
        if (item !== undefined) {
          matchedSection = cls;
          matchedItem = item;
          break;
        }
      }
      if (matchedItem === undefined || matchedSection === undefined) {
        applyShapeState(element, { matched: false });
        return;
      }
      const section = matchedSection;
      applyShapeState(element, {
        matched: true,
        available: isItemAvailable(matchedItem),
        selected: selectedNames.some((name) => nameMatchesSection(section, name, sections)),
        label: getItemSectionName(matchedItem) ?? section,
        decorative: element.getAttribute(DECORATIVE_ATTRIBUTE) === 'true',
      });
    });
  }, [load, sections, selectedItems, items, isItemAvailable, getItemSectionName]);

  // Resolve the section name from an event target by walking up the DOM until
  // an interactive shape is found. Prefers tokens that map to a real item so
  // multi-class shapes (e.g. `class="yellow_zone st0"`) don't get matched
  // against styling helpers like `st0`.
  const resolveSectionFromEvent = (event: Event): string | null => {
    const container = containerRef.current;
    if (!container) {
      return null;
    }
    let element = event.target as Element | null;
    while (element && element !== container) {
      if (element.matches?.(INTERACTIVE_SHAPE_SELECTOR)) {
        const className = (element.getAttribute('class') ?? '').trim();
        if (className) {
          const tokens = className.split(/\s+/);
          const itemMatch = tokens.find((t) => findItemForSection(t) !== undefined);
          if (itemMatch) {
            return itemMatch;
          }
          const sectionMatch = tokens.find((t) => sections.includes(t));
          if (sectionMatch) {
            return sectionMatch;
          }
        }
      }
      element = element.parentElement;
    }
    return null;
  };

  // Click + keyboard handler (event delegation). Keyboard activation via
  // Enter/Space mirrors click so the map is reachable with tabindex="0".
  useEffect(() => {
    const container = containerRef.current;
    if (!container || load.kind !== 'interactive' || sections.length === 0) {
      return;
    }

    const trigger = (event: Event) => {
      const sectionName = resolveSectionFromEvent(event);
      if (!sectionName) {
        return;
      }
      // Single enforcement point: only a matched, currently bookable section
      // selects. Sold-out sections and decoration never fire.
      const item = findItemForSection(sectionName);
      if (item !== undefined && isItemAvailable(item)) {
        onSelectItem(item);
      }
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      const target = event.target as Element | null;
      if (!target?.matches?.('[data-available="true"]')) {
        return;
      }
      event.preventDefault();
      trigger(event);
    };

    container.addEventListener('click', trigger);
    container.addEventListener('keydown', onKeyDown);
    return () => {
      container.removeEventListener('click', trigger);
      container.removeEventListener('keydown', onKeyDown);
    };
  }, [load, sections, items, isItemAvailable, getItemSectionName, onSelectItem]);

  // Hover handlers (event delegation) — propagate :hover from one shape to all
  // shapes that share the same class. Native CSS :hover only matches the
  // element under the cursor, but a single AccessType may have N polygons.
  useEffect(() => {
    const container = containerRef.current;
    if (!container || load.kind !== 'interactive' || sections.length === 0) {
      return;
    }

    let currentHovered: string | null = null;
    const queryForSection = (sectionName: string) => {
      const escaped = sectionName.replace(/"/g, '\\"');
      return INTERACTIVE_SHAPE_SELECTOR.replace(/\[class\]/g, `[class~="${escaped}"]`);
    };

    const setHover = (sectionName: string | null) => {
      if (sectionName === currentHovered) {
        return;
      }
      if (currentHovered) {
        container
          .querySelectorAll(queryForSection(currentHovered))
          .forEach((element) => element.removeAttribute('data-hover'));
      }
      if (sectionName) {
        container
          .querySelectorAll(queryForSection(sectionName))
          .forEach((element) => element.setAttribute('data-hover', 'true'));
      }
      currentHovered = sectionName;
    };

    // Only real sections hover; decoration stays untouched.
    const onOver = (event: Event) => {
      const sectionName = resolveSectionFromEvent(event);
      const isSection = sectionName !== null && findItemForSection(sectionName) !== undefined;
      setHover(isSection ? sectionName : null);
    };
    const onOut = (event: MouseEvent) => {
      // If the pointer is moving to another shape in the same section group,
      // keep the current hover state.
      const next = event.relatedTarget as Element | null;
      if (next && currentHovered) {
        const still = next.closest?.(queryForSection(currentHovered));
        if (still) {
          return;
        }
      }
      // Otherwise clear. If the pointer moves to another shape, `mouseover`
      // on that shape will set the new hover. Using `resolveSectionFromEvent`
      // here would read `event.target` (the shape being exited) and re-apply
      // the same hover, leaving a sticky highlight when leaving the map.
      setHover(null);
    };

    container.addEventListener('mouseover', onOver);
    container.addEventListener('mouseout', onOut as EventListener);
    return () => {
      container.removeEventListener('mouseover', onOver);
      container.removeEventListener('mouseout', onOut as EventListener);
      setHover(null);
    };
  }, [load, sections, items, isItemAvailable, getItemSectionName]);

  return (
    <div
      className={clsx(
        styles.heroSection,
        // Pin only the interactive SVG map so it stays visible while the
        // selection list scrolls beneath it. Static images / hero scroll away.
        load.kind === 'interactive' && styles.heroSectionSticky
      )}
      // Marks the pinned map so the page can offset scroll-into-view by its
      // height (keeps the selected card from landing behind the sticky map).
      data-venue-map-root={load.kind === 'interactive' ? '' : undefined}
    >
      <div className={styles.heroImageWrapper}>
        {load.kind === 'interactive' && (
          // SVG is sanitized server-side at upload AND again here at render
          // (sanitizeSvg above) — defense in depth. Both gates use the same
          // allowlist; divergence is a regression.
          <div ref={containerRef} className={styles.svgContainer} />
        )}
        {load.kind === 'static' && (
          <img src={imageUrl} alt={imageAlt} className={styles.staticImage} />
        )}
        {load.kind === 'loading' && <div ref={containerRef} className={styles.svgPlaceholder} />}
        {load.kind === 'error' && <div ref={containerRef} className={styles.svgPlaceholder} />}
      </div>
      {load.kind === 'interactive' && (
        <div className={styles.legendHint}>Tap a section on the map or pick a ticket below</div>
      )}
    </div>
  );
}

export default VenueMap;
